import { isTimePermissionEnabled } from './applicationFeatures';
import type {
  ReportDefinition,
  ReportFilterDefinition,
  ReportOutputFormat,
} from '../types/report';

/**
 * Local filter metadata for confirmed backend report codes.
 *
 * Catalogue listing comes from GET /api/v1/reports (mounted).
 * That endpoint does not return filter definitions, so filters below
 * mirror arkhrmsapi report.validator.js validateFilters().
 *
 * Source: /Users/applelifeplus/arkhrmsapi/src/validators/report.validator.js
 * Report codes: REPORT_CODES in the same file.
 * Do not expose stored-procedure names.
 */

export const CONFIRMED_REPORT_CODES = [
  'ATTENDANCE_REGISTER',
  'DAILY_ATTENDANCE_SUMMARY',
  'MONTHLY_ATTENDANCE_SUMMARY',
  'EMPLOYEE_DIRECTORY',
  'LEAVE_REGISTER',
  'LEAVE_BALANCE',
  'MONTHLY_LOP',
  'WFH_SUMMARY',
  'PERMISSION_SUMMARY',
  'LATE_ARRIVAL_DEDUCTIONS',
] as const;

export type ConfirmedReportCode = (typeof CONFIRMED_REPORT_CODES)[number];

const DATE_RANGE_FILTERS: ReportFilterDefinition[] = [
  { field: 'fromDate', label: 'From date', type: 'DATE', required: true },
  { field: 'toDate', label: 'To date', type: 'DATE', required: true },
];

const DEPARTMENT_FILTER: ReportFilterDefinition = {
  field: 'departmentId',
  label: 'Department ID',
  type: 'NUMBER',
  required: false,
  minimum: 1,
};

type LocalReportMeta = {
  reportCode: ConfirmedReportCode;
  reportName: string;
  description: string;
  category: string;
  filters: ReportFilterDefinition[];
  /** Backend has no max date-range days; only toDate >= fromDate. */
  maximumDateRangeDays: null;
};

/**
 * Fixed metadata keyed by confirmed report codes only.
 * Formats are filled from the backend list response + user permissions.
 */
export const REPORT_FILTER_CATALOGUE: Record<
  ConfirmedReportCode,
  LocalReportMeta
> = {
  ATTENDANCE_REGISTER: {
    reportCode: 'ATTENDANCE_REGISTER',
    reportName: 'Attendance Register',
    description: 'Attendance records for a date range.',
    category: 'ATTENDANCE',
    filters: [...DATE_RANGE_FILTERS, DEPARTMENT_FILTER],
    maximumDateRangeDays: null,
  },
  LATE_ARRIVAL_DEDUCTIONS: {
    reportCode: 'LATE_ARRIVAL_DEDUCTIONS',
    reportName: 'Late Arrival Deductions',
    description: 'Late arrivals with the leave and LOP deducted for each.',
    category: 'ATTENDANCE',
    filters: [...DATE_RANGE_FILTERS, DEPARTMENT_FILTER],
    maximumDateRangeDays: null,
  },
  DAILY_ATTENDANCE_SUMMARY: {
    reportCode: 'DAILY_ATTENDANCE_SUMMARY',
    reportName: 'Daily Attendance Summary',
    description: 'Summary figures for one attendance date.',
    category: 'ATTENDANCE',
    filters: [
      {
        field: 'attendanceDate',
        label: 'Attendance date',
        type: 'DATE',
        required: true,
      },
    ],
    maximumDateRangeDays: null,
  },
  MONTHLY_ATTENDANCE_SUMMARY: {
    reportCode: 'MONTHLY_ATTENDANCE_SUMMARY',
    reportName: 'Monthly Attendance Summary',
    description: 'Attendance summary for one month.',
    category: 'ATTENDANCE',
    filters: [
      {
        field: 'year',
        label: 'Year',
        type: 'NUMBER',
        required: true,
        minimum: 1000,
        maximum: 9999,
      },
      {
        field: 'month',
        label: 'Month',
        type: 'NUMBER',
        required: true,
        minimum: 1,
        maximum: 12,
      },
    ],
    maximumDateRangeDays: null,
  },
  EMPLOYEE_DIRECTORY: {
    reportCode: 'EMPLOYEE_DIRECTORY',
    reportName: 'Employee Directory',
    description: 'Employee directory for the organization.',
    category: 'EMPLOYEE',
    filters: [],
    maximumDateRangeDays: null,
  },
  LEAVE_REGISTER: {
    reportCode: 'LEAVE_REGISTER',
    reportName: 'Leave Register',
    description: 'Leave applications for a date range.',
    category: 'LEAVE',
    filters: [...DATE_RANGE_FILTERS, DEPARTMENT_FILTER],
    maximumDateRangeDays: null,
  },
  LEAVE_BALANCE: {
    reportCode: 'LEAVE_BALANCE',
    reportName: 'Leave Balance',
    description: 'Leave balances for a calendar year.',
    category: 'LEAVE',
    filters: [
      {
        field: 'year',
        label: 'Year',
        type: 'NUMBER',
        required: true,
        minimum: 1000,
        maximum: 9999,
      },
    ],
    maximumDateRangeDays: null,
  },
  MONTHLY_LOP: {
    reportCode: 'MONTHLY_LOP',
    reportName: 'Monthly Loss of Pay',
    description:
      'LOP per employee for one month: applied LOP, negative leave balance, leave shortfall and late arrivals.',
    category: 'LEAVE',
    filters: [
      {
        field: 'year',
        label: 'Year',
        type: 'NUMBER',
        required: true,
        minimum: 1000,
        maximum: 9999,
      },
      {
        field: 'month',
        label: 'Month',
        type: 'NUMBER',
        required: true,
        minimum: 1,
        maximum: 12,
      },
    ],
    maximumDateRangeDays: null,
  },
  WFH_SUMMARY: {
    reportCode: 'WFH_SUMMARY',
    reportName: 'WFH Summary',
    description: 'Work-from-home summary for a date range.',
    category: 'WFH',
    filters: [...DATE_RANGE_FILTERS],
    maximumDateRangeDays: null,
  },
  PERMISSION_SUMMARY: {
    reportCode: 'PERMISSION_SUMMARY',
    reportName: 'Permission Summary',
    description: 'Time-permission summary for a date range.',
    category: 'PERMISSION',
    filters: [...DATE_RANGE_FILTERS],
    maximumDateRangeDays: null,
  },
};

export const REPORT_PREVIEW_MAX_ROWS = 50;

export function isConfirmedReportCode(
  value: string,
): value is ConfirmedReportCode {
  return (isTimePermissionEnabled() || value !== 'PERMISSION_SUMMARY') &&
    (CONFIRMED_REPORT_CODES as readonly string[]).includes(value);
}

export function getLocalReportMeta(
  reportCode: string,
): LocalReportMeta | null {
  if (!isConfirmedReportCode(reportCode)) return null;
  return REPORT_FILTER_CATALOGUE[reportCode];
}

export function mergeReportDefinition(
  reportCode: string,
  reportName: string,
  category: string | null,
  supportedFormats: ReportOutputFormat[],
): ReportDefinition | null {
  const meta = getLocalReportMeta(reportCode);
  if (!meta) {
    if (__DEV__) {
      console.warn('[reports] Ignoring unknown report code from API');
    }
    return null;
  }

  const formats = supportedFormats.filter(
    (format): format is ReportOutputFormat =>
      format === 'JSON' || format === 'CSV' || format === 'XLSX',
  );

  return {
    reportCode: meta.reportCode,
    reportName: reportName || meta.reportName,
    description: meta.description,
    category: category || meta.category,
    supportedFormats: formats,
    filters: meta.filters,
    maximumDateRangeDays: meta.maximumDateRangeDays,
    previewSupported: formats.includes('JSON'),
    exportSupported: formats.includes('CSV') || formats.includes('XLSX'),
  };
}
